import React, {useEffect, useMemo, useState} from 'react'
import {PrimitiveType, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {keyframes} from '@emotion/react'
import styled from '@emotion/styled'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {getCurrencyCode, getIsRtlLanguage} from 'modules/meta/selectors'
import {OverlayType} from 'modules/overlay/types'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {AnimatePresence, motion, useReducedMotion} from 'motion/react'

import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {usePriceHistory} from '../../hooks/usePriceHistory'
import {usePriceInsightsEligibility} from '../../hooks/usePriceInsightsEligibility'
import {usePriceTrend} from '../../hooks/usePriceTrend'
import {DetailedPrice} from '../../services/priceInsightsApi'
import {PriceTrendContent} from '../../types'
import {priceTrendMessages} from '../../utils/messages'
import {normalizeFetchParams} from '../../utils/validation'
import {AnimatedSparkles} from './AnimatedSparkles'

const MIN_LOADING_DURATION = 2000

type BannerState =
  | 'loading'
  | 'goodDeal'
  | 'fairPrice'
  | 'alternatives'
  | 'pricesDropped'
  | 'pricesStable'
  | 'pricesIncreased'
  | 'noData'

interface BannerConfig {
  messageKey: keyof typeof priceTrendMessages
  values?: Record<string, PrimitiveType>
}

export const getBannerState = (
  isLoading: boolean,
  areOffersLoaded: boolean,
  hasMinLoadingElapsed: boolean,
  content: PriceTrendContent | null,
  isError: boolean
): BannerState => {
  if (isLoading || !areOffersLoaded || !hasMinLoadingElapsed) return 'loading'
  if (!content || isError) return 'noData'

  const {messageKey} = content.recommendation
  if (messageKey === 'recommendationCheaperThanSimilar') return 'goodDeal'
  if (messageKey === 'recommendationHigherThanSimilar') return 'alternatives'
  if (messageKey === 'recommendationTypicalInArea') return 'fairPrice'
  if (messageKey === 'recommendationPricesDropped') return 'pricesDropped'
  if (messageKey === 'recommendationPricesStable') return 'pricesStable'
  if (messageKey === 'recommendationPricesIncreased') return 'pricesIncreased'
  return 'fairPrice'
}

export const getBannerConfig = (
  state: BannerState,
  content: PriceTrendContent | null
): BannerConfig => {
  switch (state) {
    case 'goodDeal': {
      const percent = content?.similarHotels?.title.values?.percent
      if (percent === undefined) return {messageKey: 'priceInsightsBannerFair'}
      return {
        messageKey: 'priceInsightsBannerCheaper',
        values: {percent}
      }
    }
    case 'fairPrice':
      return {messageKey: 'priceInsightsBannerFair'}
    case 'alternatives':
      return {messageKey: 'priceInsightsBannerAlternatives'}
    case 'pricesDropped':
      return {messageKey: 'recommendationPricesDropped'}
    case 'pricesStable':
      return {messageKey: 'recommendationPricesStable'}
    case 'pricesIncreased':
      return {messageKey: 'recommendationPricesIncreased'}
    case 'noData':
    default:
      return {messageKey: 'priceInsightsBannerNoData'}
  }
}

// Reserve the natural banner height (padding + bodyS line-height = 38px) even
// when content is empty — otherwise `hideLoadingContent` collapses the shell
// and the dark band shrinks for ~2s before the real copy wipes in.
const BANNER_MIN_HEIGHT = 38

const BannerShell = styled.div<{hasChevron: boolean}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${BANNER_MIN_HEIGHT}px;
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s400};
  padding-right: ${({hasChevron}) =>
    hasChevron ? '40px' : cssTheme.layout.spacing.s400};
  background-color: ${cssTheme.colors.background.brand.inverse};
  border-radius: ${cssTheme.layout.radius.md} ${cssTheme.layout.radius.md} 0 0;
  cursor: ${({hasChevron}) => (hasChevron ? 'pointer' : 'default')};
  overflow: hidden;

  &[aria-busy='true'] {
    cursor: default;
  }
`

const ChevronWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${cssTheme.layout.spacing.s300};
  z-index: 2;
`

// Two gradient bands sweep back-to-back ("one, two"), then rest offscreen for
// ~1000ms before the pair repeats. Each band sweeps in 2000ms (66.67% of the
// 3000ms cycle); the remainder of the cycle holds the band parked offscreen.
const shimmerBand = keyframes`
  0% { transform: translateX(-100%); }
  66.67% { transform: translateX(250%); }
  100% { transform: translateX(250%); }
`

const ShimmerBackground = styled(motion.div)<{isRtl: boolean}>`
  position: absolute;
  inset: 0;
  background-color: ${cssTheme.colors.background.brand.inverse};
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  transform: ${({isRtl}) => (isRtl ? 'scaleX(-1)' : 'none')};

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 40%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${cssTheme.colors.brand.c800} 50%,
      transparent 100%
    );
    animation: ${shimmerBand} 3000ms cubic-bezier(0.5, 0, 0, 1) infinite;
    // Apply the 0% keyframe (offscreen-left) during animation-delay so the
    // band is not visible parked at left:0 before its first sweep starts.
    animation-fill-mode: backwards;
  }

  // Band 1 starts at mount; band 2 trails by 1000ms for the "one, two" pair,
  // then both rest offscreen for ~1000ms before the cycle repeats. Positive
  // delays keep iteration 1 identical to every subsequent iteration.
  &::before {
    animation-delay: 0ms;
  }
  &::after {
    animation-delay: 1000ms;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      animation: none;
      display: none;
    }
  }
`

const wipeInLtr = keyframes`
  from { clip-path: inset(0 100% 0 0); }
  to { clip-path: inset(0 0 0 0); }
`

const wipeInRtl = keyframes`
  from { clip-path: inset(0 0 0 100%); }
  to { clip-path: inset(0 0 0 0); }
`

const ContentRow = styled(motion.div)`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${cssTheme.layout.spacing.s250};
  padding: 0 ${cssTheme.layout.spacing.s100};
  color: ${cssTheme.colors.content.neutral.inverse};
`

const LoadingContentRow = styled(ContentRow)<{isRtl: boolean}>`
  animation: ${({isRtl}) => (isRtl ? wipeInRtl : wipeInLtr)} 0.35s
    cubic-bezier(0.22, 1, 0.36, 1) forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

// Skeleton block shown next to the CheckCircle icon while the banner is held
// in its externally-driven loading state. A translucent white overlay gives
// the same subtle-highlight feel on the dark banner as standard skeletons
// give on light surfaces, without needing a brand-specific tint token.
const BannerTextSkeleton = styled.div`
  width: 220px;
  max-width: 100%;
  height: 22px;
  border-radius: ${cssTheme.layout.radius.lg};
  background-color: rgba(255, 255, 255, 0.08);
`

// Truncates the banner copy to a single line with an ellipsis when a long
// translation (e.g. DE, FR, IT) would otherwise overflow the banner and wrap
// or push the chevron off-screen.
const BannerTextEllipsis = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;

  & > * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

interface PriceInsightsBannerProps {
  hotelId: string
  areOffersLoaded: boolean
  /** When true, hides the chevron and makes the banner non-interactive.
   *  Used when the banner is rendered alongside its own CTA (e.g. the
   *  compact card has a dedicated "View more insights" button below). */
  hideChevron?: boolean
  /**
   * When true, the banner renders an empty dark shell during its internal
   * loading state — no shimmer, no "Analysing deal…" copy. Use this when the
   * caller already shows an outer skeleton covering the same pixels, so the
   * handoff from skeleton to real content doesn't flash a second loading UI.
   */
  hideLoadingContent?: boolean
  /**
   * External "not ready" signal. When true, the banner is forced into its
   * loading state regardless of its own fetch status. Use from a parent that
   * depends on additional data (e.g. the compact card's price-history call) so the
   * banner copy only appears once the whole widget has its data.
   */
  isExternallyLoading?: boolean
  /**
   * Latest similar-hotels percentile distribution in the RAW API unit
   * (full-tax stay total). When provided, the recommendation classifier
   * picks the banner copy by which percentile band the hotel's full-tax
   * total sits in (matches the `PriceRangeBar` indicator). When omitted,
   * the classifier falls back to the median ±10% comparison.
   *
   * Must be the raw, un-adjusted percentiles — `usePriceHistory` exposes
   * this as `latestDetailedPriceRaw`. Passing the tax-adjusted variant
   * produces flipped banner copy in the `showTotalPrices=true` ×
   * `includeTaxes=false` path.
   */
  detailedPrice?: DetailedPrice | null
}

export const PriceInsightsBanner = ({
  hotelId,
  areOffersLoaded,
  hideChevron = false,
  hideLoadingContent = false,
  isExternallyLoading = false,
  detailedPrice
}: PriceInsightsBannerProps) => {
  const {formatMessage} = useIntl()
  const showOverlay = useShowOverlay()
  const prefersReducedMotion = useReducedMotion()
  const isRtl = useSelector(getIsRtlLanguage)

  // Build fetch params from Redux (same pattern as PriceTrendMobile)
  const {checkIn, checkOut, rooms} = useSelector(getSearchBoxValues)
  const currency = useSelector(getCurrencyCode)
  const normalizedFetchParams = useMemo(
    () =>
      normalizeFetchParams({
        hotelId,
        checkIn,
        checkOut,
        rooms,
        currency
      }),
    [hotelId, checkIn, checkOut, rooms, currency]
  )

  // When eligible: always fetch the price-history percentiles via
  // `usePriceHistory`. RTK Query dedupes identical query args, so when the
  // compact card (which fetches at the parent level) and this banner
  // are mounted on the same page they share a single network request.
  // Prop wins when the caller passed a value (truthy or null) — the
  // explicit pass means "I already have it / I know there is none." Only
  // fall back to the self-fetched value when the prop was omitted.
  //
  // Not eligible: skip the fetch entirely so the banner falls through to the
  // median classifier.
  const {isPriceInsightsEligible} = usePriceInsightsEligibility()
  const {
    latestDetailedPriceForClassifier: fetchedDetailedPrice,
    isLoading: isSelfFetchingHistory
  } = usePriceHistory({
    ...normalizedFetchParams,
    enabled: isPriceInsightsEligible
  })
  const effectiveDetailedPrice =
    detailedPrice !== undefined ? detailedPrice : fetchedDetailedPrice

  const {content, isLoading, isError, isPrimaryOfferSplitBooking} =
    usePriceTrend({
      fetchParams: normalizedFetchParams,
      detailedPrice: effectiveDetailedPrice
    })

  // Minimum loading duration timer. Reset whenever the query key changes so
  // that a date/hotel/rooms/currency change re-applies the 2s floor.
  const [hasMinLoadingElapsed, setHasMinLoadingElapsed] = useState(false)
  useEffect(() => {
    setHasMinLoadingElapsed(false)
    const timer = setTimeout(() => {
      setHasMinLoadingElapsed(true)
    }, MIN_LOADING_DURATION)

    return () => clearTimeout(timer)
  }, [hotelId, checkIn, checkOut, rooms, currency])

  // When eligible, the parent didn't pass `detailedPrice`, and our self-fetch
  // is still in flight, hold the loading state. Otherwise `effectiveDetailedPrice`
  // would briefly be null, making `usePriceTrend` fall through to the median
  // classifier and produce a banner copy that flips to the percentile copy
  // once history resolves — a visible flicker that breaks the "banner stays in
  // sync with PriceRangeBar" contract.
  const isWaitingForOwnHistory =
    isPriceInsightsEligible &&
    detailedPrice === undefined &&
    isSelfFetchingHistory

  // When the caller already covers the loading phase with its own skeleton
  // (compact card), bypass the min-loading grace period so the real banner
  // copy lands the moment content resolves — no empty dark band in between.
  // `isExternallyLoading` also forces the loading state from the outside so
  // the banner waits for sibling data (e.g. the price-history call) before
  // revealing its copy.
  const bannerState = getBannerState(
    isLoading || isExternallyLoading || isWaitingForOwnHistory,
    areOffersLoaded,
    hasMinLoadingElapsed || hideLoadingContent,
    content,
    isError
  )

  const isLoadingState = bannerState === 'loading'
  const isNoDataState = bannerState === 'noData'
  // PriceTrend doesn't support split booking offers — the bottom sheet won't
  // open and the PriceRangeBar is suppressed, so the banner falls back to the
  // generic "Best available deal" copy and is rendered non-interactive.
  const isSplitBookingState = isPrimaryOfferSplitBooking && !isLoadingState
  const isInteractive =
    !isLoadingState && !isNoDataState && !hideChevron && !isSplitBookingState
  const config = !isLoadingState ? getBannerConfig(bannerState, content) : null
  const bannerText = isSplitBookingState
    ? formatMessage(priceTrendMessages.priceInsightsBannerNoData)
    : config
      ? formatMessage(priceTrendMessages[config.messageKey], config.values)
      : ''

  const handleTap = () => {
    showOverlay({
      type: OverlayType.PriceTrendBottomSheet,
      params: {source: 'banner'}
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleTap()
    }
  }

  // Loaded row: left-to-right wipe via motion (RTL-aware). Reduced motion → plain fade.
  const wipeInset = isRtl ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)'
  const loadedRowVariants = prefersReducedMotion
    ? {
        initial: {opacity: 0},
        animate: {
          opacity: 1,
          transition: {duration: 0.3, ease: 'easeOut' as const}
        },
        exit: {
          opacity: 0,
          transition: {duration: 0.15, ease: 'easeOut' as const}
        }
      }
    : {
        initial: {clipPath: wipeInset, opacity: 1},
        animate: {
          clipPath: 'inset(0 0 0 0)',
          transition: {
            duration: 0.35,
            ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
          }
        },
        exit: {
          opacity: 0,
          transition: {duration: 0.2, ease: 'easeOut' as const}
        }
      }

  return (
    <BannerShell
      hasChevron={isInteractive}
      onClick={isInteractive ? handleTap : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      data-id={
        isLoadingState
          ? 'price-insights-banner-loading'
          : 'price-insights-banner'
      }
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-busy={isLoadingState}
    >
      <AnimatePresence>
        {isLoadingState && !hideLoadingContent && (
          <ShimmerBackground
            key="shimmer"
            isRtl={isRtl}
            initial={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.25, ease: 'easeOut'}}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {isLoadingState && !hideLoadingContent ? (
          <LoadingContentRow
            key="loading"
            isRtl={isRtl}
            exit={{opacity: 0}}
            transition={{duration: 0.2, ease: 'easeOut'}}
          >
            <AnimatedSparkles />
            <Text variant="bodyS" colorPath="content.neutral.inverse">
              <FormattedMessageWrapper
                {...priceTrendMessages.priceInsightsBannerLoading}
              />
            </Text>
          </LoadingContentRow>
        ) : isLoadingState && hideLoadingContent ? (
          <ContentRow key="loading-skeleton">
            <Icon name="CheckCircle" colorPath="content.accent.inverse" />
            <BannerTextSkeleton data-id="price-insights-banner-text-skeleton" />
          </ContentRow>
        ) : (
          <ContentRow
            key="loaded"
            variants={loadedRowVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Icon name="CheckCircle" colorPath="content.accent.inverse" />
            <BannerTextEllipsis>
              <Text variant="bodyS" colorPath="content.neutral.inverse">
                {bannerText || '\u00A0'}
              </Text>
            </BannerTextEllipsis>
          </ContentRow>
        )}
      </AnimatePresence>

      {isInteractive && (
        <ChevronWrapper style={isRtl ? {transform: 'scaleX(-1)'} : undefined}>
          <Icon name="ChevronRight" colorPath="content.neutral.inverse" />
        </ChevronWrapper>
      )}
    </BannerShell>
  )
}
